"use client";

import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
/* ⚠ TYPE-ONLY, SO THE LAZY SPLIT SURVIVES IT: `import type` is erased before
   the bundler sees it. A value import here would defeat the boundary below. */
import type { AiSearchFieldClientProps } from "../ai-search-field/ai-search-field.client";
import { SearchFieldClient } from "../../atoms/search-field/search-field.client";
/* Type-only, and imported rather than restated: the rim scale is the ATOM's, so
   a second copy here would drift the first time a step is added. SURFACE_PRESETS
   below follows the same rule, from tokens/surface-presets. */
import type { RimPreset } from "../../atoms/search-field/search-field.shared";
import { CLOSE_GLYPH_PATH, CLOSE_GLYPH_VIEWBOX } from "../../atoms/close-glyph";
import { INERT_SLOT } from "../../atoms/inert-slot";
import { fireErrorDisplayed, fireLinkClick, firePageView, fireSearch } from "../../services/analytics";
import { currentPageUrl } from "../../services/page-url";
import { isSafeHref } from "../../services/safe-href";
import {
  SURFACE_PRESETS,
  isSurfacePreset,
  resolveSurface,
  resolveSurfaceAttribute,
  type SurfacePreset
} from "../../tokens/surface-presets";
import styles from "./search-panel.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * ⚠ `-input` IS SUFFIXED HERE RATHER THAN PASSED. The AI branch hands the field
 * the panel's BARE `id`, which `AiSearchFieldClient` suffixes itself; this atom
 * puts `id` straight on the input, so a bare one would share the panel div's id
 * and `htmlFor` would resolve to the div, leaving the input unnamed.
 *
 * ⚠ THE VALUE ARM IS FORWARDED, NOT COERCED. `value ?? ""` reads as a
 * safeguard and would be its opposite: `SearchFieldClient` freezes its mode at
 * mount, so an uncontrolled caller would be handed a field pinned at "" with
 * its `defaultValue` dropped and its keystrokes going nowhere.
 *
 * The panel always passes `value`, so the second arm is for the type rather
 * than for a caller; anything beyond the eight props the AI branch passes is
 * deliberately dropped.
 */
function AiImportFallbackField(props: AiSearchFieldClientProps) {
  return (
    <SearchFieldClient
      action={props.action}
      autoFocus={props.autoFocus}
      id={`${props.id}-input`}
      label={props.label}
      name={props.name}
      surface={props.surface}
      {...(props.value !== undefined ?
        {
          value: props.value,
          onValueChange: props.onValueChange
        } :
        {
          defaultValue: props.defaultValue,
          onValueChange: props.onValueChange
        })}
    />
  );
}

/**
 * The AI field, lazy so `variant="standard"` does not carry it on every page.
 *
 * ⚠ The `.catch` is the error boundary this branch does not have: `<Suspense>`
 * catches suspension, not rejection, and `lazy` re-throws during render, so a
 * rejected import would take the host page down. Falling back to the standard
 * field degrades that to a plain pill: none of the AI affordances, and no clear
 * either — `clearable` is the standard branch's own choice below, and this
 * stands in for the AI field, which never had one. Re-thrown in development,
 * where it is actionable.
 */
const AiSearchFieldClient = lazy<React.ComponentType<AiSearchFieldClientProps>>(
  () => import("../ai-search-field/ai-search-field.client")
    .then(module => ({ default: module.AiSearchFieldClient }))
    .catch((error: unknown) => {
      if (process.env.NODE_ENV !== "production") {
        throw error;
      }
      console.error(
        `[SearchPanel] the AI search field import rejected, so the panel is ` +
          `rendering the standard search field rather than taking the page down.`,
        error
      );
      return { default: AiImportFallbackField };
    })
);

/** One suggestion row: what it says, where it goes, and which section it came from. */
export interface SearchPanelResult {
  /** The words the reader sees, and the accessible name of the row. */
  label: string;
  /** Where the row goes. A real anchor, so it can be opened in a new tab. */
  href: string;
  /**
   * Which section of results this row came from — "Product and Service",
   * "Support" (AC-8). Renders as a section header above the run of rows that
   * share it — consecutive only; the list is walked once and a new section
   * opens whenever this differs from the row before it, never collected by
   * value, so the server's own ranking (OQ-5) survives. It also becomes the
   * reported position, "modal_globalsearch - [group, lowercased]"; a row
   * without one is a direct link, renders under no header, and reports
   * "modal_globalsearch" alone.
   */
  group?: string;
}

/* The reported vocabulary, as constants rather than literals at four call
   sites. Exported so a spec asserts the wiring rather than a retyped copy. */
/** `searchSource` on every search this panel reports. */
export const SEARCH_PANEL_SEARCH_SOURCE = "globalsearch";
/** `searchCategory` for the standard field. */
export const SEARCH_PANEL_STANDARD_CATEGORY = "search component";
/** `searchCategory` for the Sierra AI field. */
export const SEARCH_PANEL_AI_CATEGORY = "ai search component";
/**
 * `linkPosition` for everything inside the panel — a row before any group
 * suffix, the clear and the close — and the panel's `page_view` identity
 * (HIM-4776). The `modal_` prefix is the page-view naming rule.
 */
export const SEARCH_PANEL_LINK_POSITION = "modal_globalsearch";
// Generated by Copilot- Start - 2026-10-06 11:14:48
/** The panel's virtual path, appended to the page URL it opened over. */
export const SEARCH_PANEL_VIRTUAL_PATH = "globalsearch";

/** `[page absolute URL]/globalsearch` — the destination the panel's open reports. */
export function globalSearchDestination(): string {
  return `${currentPageUrl().replace(/\/+$/, "")}/${SEARCH_PANEL_VIRTUAL_PATH}`;
}
// Generated by Copilot- End - 2026-10-06 11:14:48

// Generated by Copilot- Start - 2026-10-06 13:28:03
/**
 * Where an in-panel control keeps its report, read by the panel's own click
 * handler. ⚠ NOT `data-linkposition` / `data-linkdestination`: the container
 * fires on those, naming the click from `aria-label` or text and never
 * lowercasing, so they would add a second, wrongly named `link_click`
 * (HIM-4776 AC12). Without them it finds one field of three and skips.
 */
export const REPORT_LINK_NAME = "data-report-linkname";
export const REPORT_LINK_POSITION = "data-report-linkposition";
/** Absent means "the page the reader is on", resolved at click time. */
export const REPORT_LINK_DESTINATION = "data-report-linkdestination";
// Generated by Copilot- End - 2026-10-06 13:28:03

/**
 * What a host that resolved WITHOUT an outcome of its own is reported as. The
 * panel is the layer that knows: it awaited `onSubmit` and the promise
 * resolved. `services/analytics.ts` never sees the promise, so it cannot
 * default this. Frozen because it is exported.
 */
export const SEARCH_PANEL_AI_SUCCESS: SearchPanelAiOutcome = Object.freeze({
  errorCode: "200",
  errorMessage: "success"
});

/**
 * The `errorCode` on a rejection, which by definition has no backend status.
 *
 * ⚠ NOT `""`. `fireSearch` strips a blank code, so a failure whose message the
 * host also left blank would push exactly the object a success pushes.
 */
export const SEARCH_PANEL_AI_UNKNOWN_CODE = "unknown";

/**
 * The `errorMessage` on a resolution that MEANT to be an outcome and missed.
 * Paired with `SEARCH_PANEL_AI_UNKNOWN_CODE`: `fireSearch` strips a blank
 * message, so a code with no message would arrive as half a row.
 */
export const SEARCH_PANEL_AI_UNKNOWN_MESSAGE = "unrecognised outcome";

/**
 * How long the AI arm waits for the host before it reports the search anyway.
 *
 * ⚠ An unbounded await reports nothing at all: the AI arm reports on SETTLE, so
 * a host whose fetch stalls silently undercounts the failures most worth
 * counting. The timer is cleared as soon as the race settles, and unmount
 * takes the same rule under `abandoned` — see `AI_UNMOUNTED`.
 */
export const SEARCH_PANEL_AI_TIMEOUT_MS = 10000;

/**
 * The rejection the timeout injects, as ONE shared instance: `failureClass`
 * tells it from the host's own throw by IDENTITY, never by message.
 */
const AI_TIMED_OUT = new Error("[SearchPanel] onSubmit did not settle in time");

/**
 * The rejection UNMOUNT injects, for the exit `AI_TIMED_OUT` does not cover.
 *
 * ⚠ IT SETTLES THE RACE RATHER THAN CANCELLING IT. `bounded` settles only
 * through its own timer, so clearing the timer alone leaves `Promise.race`
 * pending forever whenever the host promise never settles either — and a
 * pending race retains the term, `reportSearch` and the panel's props.
 */
const AI_UNMOUNTED = new Error("[SearchPanel] the panel unmounted before onSubmit settled");

/**
 * One AI submit still in flight. `timer` is cleared on settle, so a finished
 * race leaves no pending work; `reject` is the exit unmount uses.
 */
interface PendingAiRace {
  timer: ReturnType<typeof setTimeout>;
  reject: (reason: unknown) => void;
}
/**
 * How the AI search's backend answered. Both fields are required — a code with
 * no message is a failure nobody can identify — so the host resolves
 * `onSubmit` with both or with nothing.
 */
export interface SearchPanelAiOutcome {
  /**
   * The backend's status as a STRING — `"200"` on success. Not a verbatim
   * pass-through: `fireSearch` slices this and `errorMessage` to
   * `REPORTED_FIELD_MAX_LENGTH`, its own shared bound, on the way out.
   */
  errorCode: string;
  /** `"success"`, or the failure message the backend returned. Bounded — see `errorCode`. */
  errorMessage: string;
}

/**
 * The absolute, lowercased destination a row reports.
 *
 * ⚠ Resolved against the LIVE PAGE, not a constant origin: a hardcoded
 * www.directv.com would label every click made on test1 or in Storybook as
 * production traffic. An unresolvable href falls back to the current page URL.
 */
function absoluteDestination(href: string): string {
  try {
    return new URL(href, window.location.href).href.toLowerCase();
  } catch {
    return window.location.href.toLowerCase();
  }
}

/**
 * Blank is absent, on the one field a feed can round-trip as whitespace: a
 * `group` of " " is truthy and would report the position "modal_globalsearch - ",
 * filing a direct link as a search result.
 */
function rowLinkPosition(group?: string): string {
  if (typeof group !== "string" || group.trim() === "") {
    return SEARCH_PANEL_LINK_POSITION;
  }
  return `${SEARCH_PANEL_LINK_POSITION} - ${group.trim().toLowerCase()}`;
}

/**
 * True for a value a host resolved a real outcome with, which is what picks
 * between reporting verbatim, coercing a near miss, and the bare-success
 * default. ⚠ Non-blank, not merely `typeof === "string"`: `fireSearch` strips a
 * blank code and message, so `{ errorCode: "", errorMessage: "" }` would take
 * the verbatim arm and push byte-for-byte the row a success pushes.
 */
const isAiOutcome = (value: unknown): value is SearchPanelAiOutcome => typeof value === "object" &&
  value !== null &&
  isNonBlankString((value as SearchPanelAiOutcome).errorCode) &&
  isNonBlankString((value as SearchPanelAiOutcome).errorMessage);

/** A string a report can actually carry: present, and not just whitespace. */
const isNonBlankString = (value: unknown): value is string => typeof value === "string" && value.trim() !== "";

/**
 * One field of a near-miss outcome, coerced to something a report can carry —
 * `{ errorCode: 500 }` survives as `"500"` rather than being discarded. Only
 * finite numbers and non-blank strings qualify; `NaN`, a boolean and an object
 * all coerce to text nobody can query. ⚠ A RESOLVED message passes through
 * verbatim where a REJECTED one does not — see `failureClass`.
 */
function outcomeField(value: unknown): string | undefined {
  if (isNonBlankString(value)) {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

/**
 * Which part of a near miss to name, so the host knows what to change: a field
 * that is ABSENT, one present with the WRONG TYPE (`200` vs `"200"`), or one
 * present but BLANK. Naming the wrong fault sends them to a correct field.
 */
function describeNearMiss(value: unknown): string {
  if (typeof value !== "object" || value === null) {
    return `a ${typeof value}, not an object`;
  }
  const near = value as { errorCode?: unknown; errorMessage?: unknown };
  const faults = (["errorCode", "errorMessage"] as const)
    .filter(key => !isNonBlankString(near[key]))
    .map(key => {
      if (near[key] === undefined || near[key] === null) {
        return `no ${key}`;
      }
      return typeof near[key] === "string" ?
        `a blank ${key}` :
        `${key} as a ${typeof near[key]}, not a string`;
    });
  return `an object with ${faults.join(" and ")}`;
}

/**
 * A resolution that MEANT to be an outcome and missed, reported in development.
 * The natural shape for a host reading `response.status` is a NUMBER, which
 * `isAiOutcome` rejects — silently, since the search still reports and the
 * dashboards look healthy. No object guard here: the nullish case, which is a
 * legitimate success, is filtered at the one call site instead.
 */
function warnNearMissOutcome(id: string, value: unknown): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  console.error(
    `[SearchPanel] "${id}" resolved onSubmit with ${describeNearMiss(value)}. ` +
      `A SearchPanelAiOutcome needs errorCode AND errorMessage, both non-blank ` +
      `STRINGS — GTM reports 200 and "200" as two values. Each field was read ` +
      `where it could be (a finite number becomes its own text), and anything ` +
      `unreadable was reported as "${SEARCH_PANEL_AI_UNKNOWN_CODE}" rather ` +
      `than as the success it is not.`
  );
}

/**
 * ⚠ A rejection reduced to a classification, because the host's own wording
 * must never reach the data layer: a `fetch` wrapper's throw carries the
 * request URL — and so the reader's search term — or an internal service name,
 * and `fireSearch` pushes to a page-global `window.dataLayer` every vendor tag
 * reads. Nothing bounds that field's CONTENT. `timeout` and `abandoned` are
 * this panel's own rejections, told apart by IDENTITY.
 */
function failureClass(error: unknown): string {
  if (error === AI_TIMED_OUT) {
    return "timeout";
  }
  if (error === AI_UNMOUNTED) {
    return "abandoned";
  }
  return error instanceof TypeError ? "network error" : "request failed";
}

/**
 * ⚠ Three failures, one check. A `null` ELEMENT type-checks (`strictNullChecks`
 * is off here) then throws on `row.href` during render, inside the global nav,
 * taking the whole header out. The same `href` is both an `<a href>` and the
 * target of a programmatic `.click()` on Enter, so an unvalidated `javascript:`
 * URI in a feed executes on a keypress. And a blank `label` leaves an
 * arrow-reachable option with no accessible name. Filtered rather than thrown:
 * one malformed row costs that row, not the header.
 */
const isSafeRow = (row?: SearchPanelResult): row is SearchPanelResult => {
  if (typeof row?.label !== "string" || typeof row?.href !== "string") {
    return false;
  }
  // WAT-11876: trimmed, not just `=== ""` — an unset label round-trips through
  // a feed as a lone space as readily as an empty string.
  if (row.label.trim() === "") {
    return false;
  }
  /* The scheme rule is the shared one, services/safe-href.ts, so the nav, the
     footer and this panel cannot drift on what counts as safe. It parses
     rather than pattern-matches, which is what makes the disguises fail. */
  return isSafeHref(row.href);
};

/**
 * Blank collapses to absent for the SAME reason `rowLinkPosition` does it: a
 * `group` a feed round-tripped as whitespace is truthy, and would otherwise
 * open a section whose header renders and announces nothing at all.
 */
function normalizedGroup(group?: string): string | undefined {
  if (typeof group !== "string" || group.trim() === "") {
    return undefined;
  }
  return group.trim();
}

/**
 * One contiguous run of rows sharing a (normalised) `group`. `rows` pairs
 * each result with its position in the FLAT `rows` array — not its position
 * inside the section — because `optionId` and `aria-selected` are keyed off
 * that flat index and grouping must not renumber it (AC-8's own hazard:
 * arrow-key navigation and `aria-activedescendant` walk the flat list, and a
 * header is not one of its options).
 */
interface SearchPanelSection {
  group?: string;
  rows: { index: number; result: SearchPanelResult }[];
}

/**
 * One pass, opening a new section whenever `group` differs from the row
 * before it — never by collecting rows into a map keyed on `group` and
 * re-emitting them. Task 1's adapter does not sort and OQ-5 leaves ranking to
 * the server, so two rows sharing a group with a different one between them
 * stay in two sections; merging them would silently reorder the server's
 * results.
 */
function toSections(rows: SearchPanelResult[]): SearchPanelSection[] {
  return rows.reduce<SearchPanelSection[]>((sections, result, index) => {
    const group = normalizedGroup(result.group);
    const last = sections[sections.length - 1];
    if (last && last.group === group) {
      last.rows.push({
        index,
        result
      });
      return sections;
    }
    sections.push({
      group,
      rows: [{
        index,
        result
      }]
    });
    return sections;
  }, []);
}

/**
 * The glass presets `tokens/surface.css` publishes, re-exported from
 * `../../tokens/surface-presets`. Omitting the prop is the shipped render.
 * ⚠ `glass-surface` is not a no-op — it carries `rgb(16 22 31 / 56%)` where the
 * frame publishes `--slate-950 @ 72%`. `glass-2` and `glass-3` are fill-only
 * (no published radius), so the panel's own blur keeps running under them.
 */
export { SURFACE_PRESETS, isSurfacePreset, resolveSurface, resolveSurfaceAttribute };
export type { SurfacePreset };

/**
 * Everything the page hands the panel. The behavioural half — `open`,
 * `onClose`, `results`, `onQueryChange`, `onSubmit` — belongs to whatever puts
 * the panel on the page; the panel is presentational and fetches nothing.
 */
export interface SearchPanelProps {
  /**
   * Page-unique. It names the panel itself, so a disclosure trigger's
   * `aria-controls` has something to point at, and everything inside derives
   * from it — the field, the list, and each row `aria-activedescendant` needs.
   */
  id: string;
  /** Controlled: whether the panel is showing. */
  open?: boolean;
  /** Uncontrolled starting state, read once at mount. */
  defaultOpen?: boolean;
  /**
   * Fires when the reader closes the panel — the close control, or Escape.
   *
   * ⚠ FOCUS RETURN IS THE OWNER'S JOB ONLY WHEN THE OWNER OWNS `open`. Passing
   * `open` means the trigger is yours, so put focus back on it here. Leave
   * `open` off and the panel restores focus itself; see `openerRef`.
   */
  onClose?: () => void;
  /** The field's visible prompt and accessible name. */
  label?: string;
  /**
   * Where a submitted search goes when no suggestion is highlighted.
   *
   * ⚠ REQUIRED, AND DELIBERATELY WITHOUT A DEFAULT: a default endpoint this
   * application does not serve would look functional right up to the moment a
   * real reader pressed Enter. Name one that exists.
   */
  action: string;
  /** Query key the term submits under. */
  name?: string;
  /** The accessible name of the close control. Icon-only, so it needs one. */
  closeLabel?: string;
  /**
   * The suggestions to show, already fetched and already ordered. The panel
   * renders them and nothing else — no filtering, no ranking, no truncation.
   */
  results?: SearchPanelResult[];
  /** Fires on every keystroke. Debounce it on the way out, not in here. */
  onQueryChange?: (query: string) => void;
  /** Shown in place of the list when a search returned nothing. */
  emptyMessage?: string;
  /**
   * A request is in flight. Renders the loading affordance in place of the
   * list and suppresses the options, so arrow keys cannot walk stale rows.
   * ⚠ OQ-11 — no loading state is drawn in Figma. Flagged for UX.
   */
  loading?: boolean;
  /**
   * The search could not be reached. Outranks both `loading` and `results` —
   * a failed request must not also show a spinner.
   * ⚠ OQ-12 — no error state is drawn in Figma. Copy is placeholder.
   */
  errorMessage?: string;
  /**
   * The backend status behind `errorMessage`, reported with it as
   * `error_displayed`. Absent, the report carries `"unknown"`.
   */
  errorCode?: string;
  /**
   * Which field the panel holds — the standard pill, or the Sierra AI field.
   *
   * ⚠ The AI variant draws no suggestion list, and voids `results`,
   * `emptyMessage` and the live region with it: the APG combobox attributes
   * (`aria-controls`, `aria-expanded`, `aria-activedescendant`) have to sit ON
   * THE INPUT and `AiSearchFieldClient` accepts none, so rows beside it would
   * be a list no screen reader is told of and no arrow key reaches. The fix is
   * to give the AI field those attributes, not to drop them.
   */
  variant?: "standard" | "ai";
  /**
   * Handles a submitted term instead of letting the form navigate to `action`.
   *
   * ⚠ In the `ai` variant its resolution is part of the reported payload: the
   * panel WAITS for this promise and reports once it settles — an outcome
   * verbatim, nothing as `SEARCH_PANEL_AI_SUCCESS`, a rejection as
   * `SEARCH_PANEL_AI_UNKNOWN_CODE`. Omitting the prop leaves both keys absent,
   * and the `standard` variant ignores any resolution. ⚠ A blank term is still
   * handed over, but `fireSearch` gates it, so nothing is reported.
   */
  onSubmit?: (
    query: string
  ) => void | SearchPanelAiOutcome | Promise<SearchPanelAiOutcome | void>;
  /**
   * Whether the field inside the panel draws its magnifying glass.
   *
   * ⚠ OFF BY DEFAULT, WHICH OVERRIDES THE PUBLISHED FRAMES — both 12557:16654
   * and 12557:16155 draw the magnifier. A confirmed UX call, on the conflicts
   * page; not a build correction.
   */
  fieldIcon?: boolean;
  /**
   * Which glass step the FIELD inside the panel is painted with — the atom's
   * own `surface`, forwarded. Omit it for the field's published pill.
   * ⚠ Not the same prop as `surface`, which is the panel's, and they do not
   * compose: the presets INHERIT, so `tokens/surface.css` resets them at every
   * element carrying no preset of its own.
   */
  fieldSurface?: SurfacePreset | (string & {});
  /**
   * How brightly the field's rim is drawn — the atom's own `rim`, forwarded.
   * Omit it for the measured Figma value. Only that default is design; the
   * named steps are an engineering scale around it, and the panel does not
   * raise it — Figma publishes no in-panel difference.
   */
  fieldRim?: RimPreset | (string & {});
  /**
   * Substitutes the mark at the lead end of the field — the atom's own
   * `startIcon`, forwarded. Beats `fieldIcon`, which the atom warns about.
   * ⚠ There is deliberately no `fieldEndIcon`: the panel always passes
   * `clearable`, which beats `endIcon` in the atom, so it would be inert.
   */
  fieldStartIcon?: React.ReactNode;
  /**
   * Substitutes the close control's glyph; the default is the published X from
   * `atoms/close-glyph`. ⚠ The accessible name never comes from here — the slot
   * renders inside an `aria-hidden`, `inert` wrapper, so text here is visible
   * and UNANNOUNCED and anything focusable in it is a defect. An `<svg>` child
   * is scaled to the 24px box; a falsy value leaves the default X.
   */
  closeIcon?: React.ReactNode;
  /**
   * Which glass step the panel is painted with. Omit it for the published
   * surface; `glass-surface` is NOT the same as omitting — see
   * `SURFACE_PRESETS`. ⚠ This is the panel's own glass and nothing else: no
   * scrim, no backdrop. 12557:16157 shows the hero undimmed behind it.
   */
  surface?: SurfacePreset | (string & {});
  className?: string;
}

/**
 * SearchPanel — the expanded search surface the navigation opens.
 *
 * A glass panel holding a close control, the SearchField pill and the
 * suggestions the reader can arrow through. It hangs under the nav bar and
 * dims, locks and traps nothing. Pushes `search` on submit, and `link_click`
 * for a row, the clear and the close — see `REPORT_LINK_POSITION`.
 *
 * ⚠ It needs a positioned ancestor: absolutely positioned, so whatever renders
 * it must establish a containing block or the panel anchors to the document.
 * Not `fixed` — the nav slides away on scroll and it would stay behind.
 */
function SearchPanelImpl({
  id,
  open,
  defaultOpen = false,
  onClose,
  label = "Search DIRECTV",
  action,
  name = "q",
  closeLabel = "Close search",
  results,
  onQueryChange,
  emptyMessage,
  loading,
  errorMessage: rawErrorMessage,
  errorCode,
  variant = "standard",
  onSubmit,
  fieldIcon = false,
  fieldSurface,
  fieldRim,
  fieldStartIcon,
  closeIcon,
  surface,
  className
}: SearchPanelProps) {
  /* ⚠ A default only covers `undefined`. `label=""` and `closeLabel=""` sail
     through it and leave an unnamed text field and an unnamed icon-only
     button — and both values usually arrive from a CMS field that can be
     saved empty. */
  const fieldLabel = label.trim() || "Search DIRECTV";
  const closeName = closeLabel.trim() || "Close search";
  /* Same reason: a blank message would still outrank the list and render an
     empty error. */
  const errorMessage = rawErrorMessage?.trim() || undefined;

  /* ⚠ NORMALISED ONCE, then fed to both the warning and the JSX. A CMS select
     round-trips an unset value as `""`, which is not `undefined`, so
     `data-dds-surface` would render as a PRESENT empty attribute — and
     surface.css's nested reset keys off ABSENCE, so the panel would inherit an
     ancestor's fill instead of firing its own var() fallbacks. */
  const resolvedSurface = resolveSurface(surface);
  const surfaceAttribute = resolveSurfaceAttribute(surface);

  /* A preset surface.css does not publish selects no rule, so the var()
     fallbacks fire and the panel renders its default — correct, and completely
     silent. The prop admits any string for the CMS's sake, so this warning is
     the only gate. Blank is not reported: it means "not setting this". */
  if (process.env.NODE_ENV !== "production" && resolvedSurface !== undefined &&
      !isSurfacePreset(resolvedSurface)) {
    console.warn(
      `[SearchPanel] Unknown surface "${resolvedSurface}" — no glass preset matches, so the panel is rendering its published default. Expected one of: ${SURFACE_PRESETS.join(", ")}.`
    );
  }

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  /* -1 is "no row highlighted", the state Enter submits the search itself
     from. An index rather than an id, so a debounced fetch landing mid-arrow
     cannot strand the highlight on a row that has gone. */
  const [activeIndex, setActiveIndex] = useState(-1);
  /* The term last submitted since the box last changed; see `handleSubmit`. */
  const submittedTermRef = useRef<string>();
  const listboxId = `${id}-results`;
  const inputId = `${id}-input`;
  const panelRef = useRef<HTMLDivElement>(null);

  /* ⚠ Read DURING the first render, not from an effect: the field inside
     carries `autoFocus`, so by the time an effect could run, focus has already
     moved into the panel and the opener is no longer readable. Used only when
     the panel owns its own `open`. */
  const openerRef = useRef<HTMLElement | null>(
    typeof document === "undefined" ?
      null :
      (document.activeElement as HTMLElement | null)
  );

  /* Every AI race started and not yet seen settle, so unmount can finish them.
     A Set rather than one slot: the field stays interactive, so a reader can
     submit again before the first answer lands. ⚠ Unmount SETTLES each race
     rather than cancelling it — `bounded` settles only through its own timer,
     so clearing the timer alone strands `Promise.race` pending forever on a
     host promise that never settles, retaining the term and the panel's
     props. It reports as `abandoned`; nothing React-owned is touched. */
  const pendingRaces = useRef<Set<PendingAiRace> | null>(null);
  /* Lazy, because `useRef(new Set())` allocates on every render and discards
     all but the first — and this body re-runs on every keystroke. */
  const racesNow = (): Set<PendingAiRace> => {
    if (!pendingRaces.current) {
      pendingRaces.current = new Set<PendingAiRace>();
    }
    return pendingRaces.current;
  };
  useEffect(() => {
    /* Read into a local so the cleanup closes over the Set rather than over
       `.current`. The Set is created once and never replaced. */
    const races = racesNow();
    return () => {
      races.forEach(race => {
        clearTimeout(race.timer);
        race.reject(AI_UNMOUNTED);
      });
      races.clear();
    };
  }, []);

  const isOpen = open ?? uncontrolledOpen;

  // Generated by Copilot- Start - 2026-10-06 11:14:48
  /* One `page_view` per opening (AC2). The ref, not the effect's own deps, is
     what stops StrictMode's double-run reporting it twice. */
  const reportedOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      reportedOpenRef.current = false;
      return;
    }
    if (reportedOpenRef.current) {
      return;
    }
    reportedOpenRef.current = true;
    firePageView({
      linkPosition: SEARCH_PANEL_LINK_POSITION,
      linkDestination: globalSearchDestination()
    });
  }, [isOpen]);
  // Generated by Copilot- End - 2026-10-06 11:14:48

  /* `undefined` is "the owner has not answered yet"; `[]` is "answered and
     nothing matched". Collapsing them shows "no results" during the first
     in-flight window of every session. */
  const answered = results !== undefined;
  /* ⚠ ONE POLARITY, DERIVED ONCE — three spellings of this test agree only
     while the union has exactly two members. The AI variant has no list (see
     the `variant` prop), and emptying `rows` here is what makes every
     downstream derivation off by construction rather than by separate gates. */
  const isAi = variant === "ai";
  const listSupported = !isAi;
  const rows = listSupported ? (results ?? []).filter(isSafeRow) : [];

  const resultsGivenToAiField = !listSupported && results !== undefined;

  /* ⚠ IN AN EFFECT, NOT THE RENDER BODY. This body runs on every keystroke and
     twice per render under StrictMode, so a console call in it logs one line
     per character typed. Keyed on the derived boolean, so it fires once per
     change of the condition. `warn` rather than `error`: a host can reasonably
     hand the same `results` to either variant. */
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && resultsGivenToAiField) {
      console.warn(
        `[SearchPanel] "${id}" was given results with variant="ai". The AI field ` +
          `cannot carry the combobox attributes a suggestion list needs, so the ` +
          `rows are not rendered. Use variant="standard" for a list.`
      );
    }
  }, [id, resultsGivenToAiField]);

  /* Gated on the query as well as the count, so suggestions retained from a
     previous search can never render under an empty box. */
  const hasRows = query.length > 0 && rows.length > 0;
  const optionId = (index: number) => `${listboxId}-${index}`;

  /* ⚠ `hasRows` NO LONGER GUARANTEES THE `<ul>` IS IN THE DOM. `loading` and
     `errorMessage` each suppress the rendered list without changing `hasRows`
     itself — it is still needed as-is for the row count message. Every site
     that assumes the list is actually THERE (arrow/Home/End, `aria-expanded`,
     `aria-activedescendant`) has to gate on this instead, or a reader can
     arrow onto — or be pointed at — a row that is not in the document. */
  const listInteractive = hasRows && !loading && !errorMessage;

  // Generated by Copilot- Start - 2026-10-06 11:14:48
  /* `error_displayed` once per error SHOWN (AC11): keyed on what renders, so a
     failure the reader never sees — panel closed, AI variant, blank copy —
     reports nothing. The same error re-shown reports again only once it has
     really cleared: hidden while a reload is in flight is not cleared, or every
     failed retry during an outage would count twice. */
  const shownError = isOpen && listSupported && errorMessage ? errorMessage : undefined;
  const reportedErrorRef = useRef<string>();
  useEffect(() => {
    if (!shownError) {
      if (!isOpen || !loading) {
        reportedErrorRef.current = undefined;
      }
      return;
    }
    const key = `${shownError}\u0000${errorCode ?? ""}`;
    if (reportedErrorRef.current === key) {
      return;
    }
    reportedErrorRef.current = key;
    fireErrorDisplayed({
      /* Lowercased like every other reported value (HIM-4776 AC12). */
      errorMessage: shownError.toLowerCase(),
      errorCode: errorCode?.toLowerCase()
    });
  }, [shownError, errorCode, isOpen, loading]);
  // Generated by Copilot- End - 2026-10-06 11:14:48

  /* A highlight belongs to a rendered list. Without this, arrowing to a row
     and then the owner setting `loading` (a stale-results-plus-in-flight-
     refetch is the ordinary case, not an edge one) strands `activeIndex` on a
     row that no longer exists, ready to resume the moment the list comes
     back — which the reader never re-selected in the new result set. */
  useEffect(() => {
    if (!listInteractive) {
      setActiveIndex(-1);
    }
  }, [listInteractive]);

  /* A new set of suggestions is a new list, so the old index would strand the
     highlight on whichever row sits at that position now.

     ⚠ KEYED ON THE ROWS' CONTENT, NOT THE ARRAY. `[results]` compares by
     identity, and the ordinary call site is a literal that reallocates on every
     render — which wipes the highlight on each keystroke AND each arrow press,
     leaving the list unusable by keyboard while looking correct in a test. */
  const resultsKey = rows.map(row => `${row.href}|${row.label}`).join("\u0000");
  useEffect(() => {
    setActiveIndex(-1);
  }, [resultsKey]);

  const close = () => {
    setUncontrolledOpen(false);
    /* The panel keeps this state while closed — it is one mounted component
       that renders null — so the next open would show the last reader's term. */
    setQuery("");
    setActiveIndex(-1);
    /* The owner holds `results`; clearing our query without telling it leaves
       the last search's suggestions in its state, ready to come straight back. */
    onQueryChange?.("");
    onClose?.();

    /* ⚠ Focus is inside the panel that is about to stop rendering; left alone
       it falls to <body> and the next Tab starts from the top of the document.
       Restored only in the uncontrolled case — see the `onClose` doc. */
    if (open === undefined) {
      const opener = openerRef.current;
      if (opener && opener !== document.body && document.contains(opener)) {
        opener.focus();
      }
    }
  };

  /* close() covers the panel closing ITSELF; a controlled owner can also just
     stop passing `open`, which never runs it. Without this the panel is never
     unmounted (it renders null), so reopening would repaint the last term and
     highlight, and one Enter would follow a suggestion nobody chose. */
  useEffect(() => {
    if (isOpen) {
      return;
    }
    setQuery("");
    setActiveIndex(-1);
  }, [isOpen]);

  /* A highlight belongs to the query it was computed for; keyed on the rows
     alone, a response resolving out of order strands the old index. */
  useEffect(() => {
    setActiveIndex(-1);
    submittedTermRef.current = undefined;
  }, [query]);

  /* ⚠ `.results` scrolls at 25rem, and `aria-activedescendant` moves the
     SELECTION without moving focus — so the browser scrolls nothing and only
     this brings a highlighted row into view. Home and End are the worst case.
     `nearest` so an already-visible row does not jolt the list. */
  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    panelRef.current
      ?.querySelector<HTMLElement>(`#${CSS.escape(optionId(activeIndex))}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleQueryChange = (next: string) => {
    setQuery(next);
    onQueryChange?.(next);
  };

  /* One place the payload is built, for both variants and all three outcome
     shapes. `fireSearch` gates a blank term itself, so an Enter on an
     untouched field books nothing. */
  const reportSearch = (term: string, outcome?: SearchPanelAiOutcome) => {
    fireSearch({
      /* Lowercased HERE: the service reports the term verbatim and leaves
         normalising to the call site. */
      searchTerm: term.toLowerCase(),
      searchCategory:
        isAi ? SEARCH_PANEL_AI_CATEGORY : SEARCH_PANEL_STANDARD_CATEGORY,
      searchSource: SEARCH_PANEL_SEARCH_SOURCE,
      /* AC-17's zero-results signal. `rows`, not `results` — what the reader
         could actually see, after the safety filter dropped anything it
         dropped, which is the number the report is about.
         ⚠ Only when the owner has answered. Before that `results` is
         `undefined`, and reporting 0 there would file "nothing matched"
         against a search still in flight — the same undefined/[] distinction
         the panel draws for the empty message, drawn once more here.
         The AI variant has no list at all, so it reports no count rather than
         a 0 that would read as a failed search. While `loading` or
         `errorMessage` hides the list, `rows` is the previous query's, so no
         count is reported either. */
      ...(listSupported && answered && !loading && !errorMessage ?
        { searchResultCount: rows.length } :
        {}),
      /* ⚠ The two keys are named, not spread. A search API that echoes the
         query back resolves `{ errorCode, errorMessage, searchTerm }`, passes
         `isAiOutcome`, and a spread would clobber the lowercased term above —
         excess-property checking does not fire on a variable. */
      ...(outcome ?
        {
          errorCode: outcome.errorCode,
          errorMessage: outcome.errorMessage
        } :
        {})
    });
  };

  /* ⚠ Bound on the panel, not passed into the field: `SearchFieldClient`'s own
     `onSubmit` calls `preventDefault()` unconditionally, which would take away
     the zero-JS route where a search NAVIGATES to `action`. `submit` bubbles,
     so listening on the wrapper leaves the browser's default alone. Typed for
     the DIV rather than the form — `FormEvent<HTMLFormElement>` is not a legal
     handler for a div and compiles only because `strictFunctionTypes` is off. */
  const handleSubmit = (event: React.FormEvent) => {
    const term = query;

    if (!onSubmit) {
      /* Nothing to await: the form navigates to `action` and the report rides
         the `pagehide` flush `fireSearch` arms for exactly this case. Both
         outcome keys are absent because no backend was called.
         A second Enter on the same term before the navigation lands is one
         search, not two (AC4). Only here: with `onSubmit` the page stays, so
         Enter again is a deliberate re-search and reports. */
      if (submittedTermRef.current !== term) {
        reportSearch(term);
      }
      submittedTermRef.current = term;
      return;
    }

    event.preventDefault();

    /* ⚠ A NON-ASYNC HOST THROWS IN BAND, and `preventDefault` has already run —
       so without this the submit is cancelled, nothing is reported, and the
       error escapes React's dispatch. Normalising to a rejected promise lets
       both arms below handle it on the path they already have. */
    let settled: unknown;
    try {
      settled = onSubmit(term);
    } catch (error) {
      settled = Promise.reject(error);
    }

    if (!isAi) {
      /* The standard variant reports no outcome fields, so a host's return
         value is not read. ⚠ But ignoring the VALUE is not ignoring a
         REJECTION: `onSubmit` may return a promise in either variant, and
         dropping the reference leaves a rejection with no handler, raising
         `unhandledrejection` at the window. The catch swallows deliberately. */
      void Promise.resolve(settled).catch(() => undefined);
      reportSearch(term);
      return;
    }

    /* ⚠ THE HOST IS RACED, NOT AWAITED — see SEARCH_PANEL_AI_TIMEOUT_MS. The
       timer is cleared on settle either way, so a fast answer leaves no pending
       work and the specs never have to drain a timer they did not arm. */
    let race: PendingAiRace;
    const bounded = new Promise<never>((_resolve, reject) => {
      /* ⚠ Built inside the executor, which runs synchronously, so the race is
         in the set before this line returns and unmount cannot miss it. */
      race = {
        reject,
        timer: setTimeout(() => reject(AI_TIMED_OUT), SEARCH_PANEL_AI_TIMEOUT_MS)
      };
      racesNow().add(race);
    });

    Promise.race([Promise.resolve(settled), bounded]).finally(() => {
      clearTimeout(race.timer);
      racesNow().delete(race);
    }).then(
      outcome => {
        if (isAiOutcome(outcome)) {
          reportSearch(term, outcome);
          return;
        }
        /* ⚠ A bare resolution is a success and a near miss is not. An object or
           a string is the host telling us something, and filing a numeric
           `{ errorCode: 500 }` as "200"/"success" does not DROP the outcome, it
           INVERTS it into the reported success rate. Coerced field by field, so
           the 500 the host did supply survives. */
        if (outcome !== undefined && outcome !== null) {
          warnNearMissOutcome(id, outcome);
          const near = outcome as { errorCode?: unknown; errorMessage?: unknown };
          reportSearch(term, {
            errorCode: outcomeField(near.errorCode) ?? SEARCH_PANEL_AI_UNKNOWN_CODE,
            errorMessage: outcomeField(near.errorMessage) ?? SEARCH_PANEL_AI_UNKNOWN_MESSAGE
          });
          return;
        }
        reportSearch(term, SEARCH_PANEL_AI_SUCCESS);
      },
      /* ⚠ The rejection arm reports, it does not swallow — a throwing Sierra
         call is the failure most worth counting, and a single-argument `.then`
         would drop it. The NAMED unknown code, because a blank one is stripped
         by `fireSearch` and would file the failure as a success. */
      error => reportSearch(term, {
        errorCode: SEARCH_PANEL_AI_UNKNOWN_CODE,
        errorMessage: failureClass(error)
      })
    );
  };

  /* What the panel's click handler reports for a row. Kept off the
     container's own attributes — see `REPORT_LINK_POSITION`. */
  const rowReport = (row: SearchPanelResult) => ({
    // WAT-11876: trimmed, converging on link.tsx's `reportedName` — a padded
    // " ESPN " books a second GTM figure beside the unpadded one, and
    // `data-linkname` on the anchor is the only thing that reads this value.
    linkName: row.label.trim().toLowerCase(),
    linkPosition: rowLinkPosition(row.group),
    linkDestination: absoluteDestination(row.href)
  });

  /* One row, lifted out of the flat `.map()` unchanged so grouping (below)
     cannot touch what it renders or reports — `index` is the row's FLAT
     position, passed in by the caller rather than read from a section-local
     `.map()`, which is what keeps `optionId`/`aria-selected` off the number
     arrow keys and `aria-activedescendant` actually walk. */
  const renderRow = ({ index, result }: { index: number; result: SearchPanelResult }): JSX.Element => {
    /* A data attribute, not `aria-label` — the report wants the label
       lowercased and a screen reader must not. */
    const report = rowReport(result);

    return (
      /* ⚠ The <li> is stripped from the accessibility tree so the anchor is
         the listbox's own child, as the pattern requires. It stays a real
         anchor for the browser (middle-click, copy link address) and out of
         the tab order, because focus never leaves the input. Index in the
         key too: a feed may return the same label at the same href twice. */
      <li key={`${result.href}|${result.label}|${index}`} role="presentation">
        <a
          aria-selected={index === activeIndex}
          className={styles.option}
          {...{
            [REPORT_LINK_DESTINATION]: report.linkDestination,
            [REPORT_LINK_NAME]: report.linkName,
            [REPORT_LINK_POSITION]: report.linkPosition
          }}
          href={result.href}
          id={optionId(index)}
          role="option"
          tabIndex={-1}
        >
          {result.label}
        </a>
      </li>
    );
  };

  // Generated by Copilot- Start - 2026-10-06 13:28:03
  /* The one `link_click` push for everything inside the panel — row, clear,
     close (AC6, AC8, AC9). Bound on the panel because clicks bubble, so the
     atom takes no handler prop. A row picked by Enter arrives here too: the
     keydown handler clicks the anchor, which is what keeps AC7 to one event. */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const control = (event.target as Element).closest?.<HTMLElement>(`[${REPORT_LINK_POSITION}]`);
    if (!control || !panelRef.current?.contains(control)) {
      return;
    }
    fireLinkClick({
      linkName: control.getAttribute(REPORT_LINK_NAME) || "",
      linkPosition: control.getAttribute(REPORT_LINK_POSITION) || "",
      linkDestination: control.getAttribute(REPORT_LINK_DESTINATION) || currentPageUrl()
    });
  };
  // Generated by Copilot- End - 2026-10-06 13:28:03

  /* Bound on the panel, not the input. Key events bubble, so this reaches every
     control inside without the atom accepting a handler prop. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }

    if (!listInteractive) {
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      // Arrows would otherwise run the caret to the ends of the typed term.
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      /* ⚠ THE INVARIANT: activeIndex ranges over -1..rows.length-1, where -1 is
         "no row selected". `+ 1` shifts that into 0..rows.length, `% span`
         wraps, `- 1` shifts back — span carries the extra slot so the cycle
         passes back THROUGH -1 at both ends and the reader can always arrow out
         of the list to their own typing. A new row type grows span. */
      const span = rows.length + 1;
      setActiveIndex(((activeIndex + 1 + step + span) % span) - 1);
      return;
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setActiveIndex(event.key === "Home" ? 0 : rows.length - 1);
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      // The row is a real anchor, so clicking it does what a click does,
      // including whatever a router attached. Assigning location would not.
      event.preventDefault();
      panelRef.current
        ?.querySelector<HTMLAnchorElement>(`#${CSS.escape(optionId(activeIndex))}`)
        ?.click();
    }
  };

  if (!isOpen) {
    return null;
  }

  /* Singular at one: this is the one string in the component only
     screen-reader readers ever hear, which is the worst place to leave
     "1 suggestions". Unanswered says nothing rather than "Searching": only
     `loading` knows a request is actually in flight. */
  const resultCountMessage =
    !listSupported || query.length === 0 || !answered ? "" :
      hasRows ? `${rows.length} suggestion${rows.length === 1 ? "" : "s"}` :
        emptyMessage || "No suggestions";

  /* ⚠ Precedence: an error outranks a spinner outranks the row count. All
     three inputs can be true at once — a stale result set plus a failed
     refetch is reachable — and the reader must be told the newest thing that
     happened, not the oldest. Gated on `listSupported` same as
     `resultCountMessage`: the AI variant has no list for either state to
     describe, so both stay voided together rather than one leaking through. */
  const status =
    !listSupported ? "" :
      errorMessage ? errorMessage :
        loading ? "Searching" :
          resultCountMessage;

  const classes = [styles.root, className].filter(Boolean).join(" ");

  return (
  // The handler is here to catch keys from the controls inside, not to make
  // the div itself interactive — every control in it is already a real one.

    <div
      className={classes}
      data-dds-surface={surfaceAttribute}
      id={id}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onSubmit={handleSubmit}
      ref={panelRef}
    >
      {/* Reported by `handleClick` (AC9). Escape closes without a click, so
          it reports nothing. */}
      <button
        aria-label={closeName}
        className={styles.close}
        {...{
          [REPORT_LINK_NAME]: closeName.toLowerCase(),
          [REPORT_LINK_POSITION]: SEARCH_PANEL_LINK_POSITION
        }}
        onClick={close}
        type="button"
      >
        {/* Truthiness, not a null check: `closeIcon={show && <Custom />}`
            passes `false`, and an empty slot is a close button with nothing to
            look at — silently, because `aria-label` keeps the name. */}
        {closeIcon ? (
          /* ⚠ The wrapper, not the slot, carries aria-hidden, `inert` and the
             box: a caller's node cannot be trusted to have any of them, and a
             focusable node inside aria-hidden is a tab stop nobody can see. */
          <span aria-hidden="true" className={styles.closeSlot} {...INERT_SLOT}>{closeIcon}</span>
        ) : (
          <svg
            aria-hidden="true"
            className={styles.closeGlyph}
            focusable="false"
            viewBox={CLOSE_GLYPH_VIEWBOX}
          >
            <path d={CLOSE_GLYPH_PATH} fill="currentColor" />
          </svg>
        )}
      </button>

      <div className={styles.body}>
        {listSupported ? (
          <SearchFieldClient
            action={action}
            /* Both gated on `listInteractive`, not `hasRows`: this is exactly
               the pair that can point at a row or a state that is not
               rendered while `loading`/`errorMessage` suppress the list — see
               `listInteractive`'s own comment.
               ⚠ `ariaControls` is DELIBERATELY NOT gated the same way, even
               though it looks like the same class of bug. `aria-controls`
               names the combobox's popup by the APG pattern regardless of
               whether that popup is currently rendered — "names the list on
               the input, and reports it collapsed while empty" below asserts
               `aria-controls` stays put with `hasRows` false too, before any
               of this branch existed. `aria-expanded` is what tells assistive
               tech whether the named popup currently holds anything. */
            ariaActiveDescendant={listInteractive && activeIndex >= 0 ? optionId(activeIndex) : undefined}
            ariaControls={listboxId}
            ariaExpanded={listInteractive}
            autoFocus
            clearable
            clearLinkPosition={SEARCH_PANEL_LINK_POSITION}
            icon={fieldIcon}
            id={inputId}
            label={fieldLabel}
            name={name}
            rim={fieldRim}
            startIcon={fieldStartIcon}
            surface={fieldSurface}
            onValueChange={handleQueryChange}
            value={query}
          />
        ) : (
          /* `id={id}` is not a collision with the panel div: AiSearchFieldClient
             renders nothing carrying its `id` bare, it derives `${id}-input`
             and `${id}-ring` — so the AI input lands on the same `${id}-input`
             the standard branch uses.

             ⚠ NO `searchCategory` IS PASSED, DELIBERATELY: that prop turns the
             field's OWN `fireSearch` on, and the search would report twice.
             The panel reports; the field stays quiet. */
          <Suspense fallback={null}>
            <AiSearchFieldClient
              action={action}
              autoFocus
              id={id}
              label={fieldLabel}
              name={name}
              surface={fieldSurface}
              onValueChange={handleQueryChange}
              value={query}
            />
          </Suspense>
        )}

        {/* ⚠ Precedence lives here too, and gated on `listSupported` for the
            same reason as `status` above: the AI variant has no list, so none
            of the three states below has anything to replace. */}
        {listSupported && (
          errorMessage ? (
            <p className={styles.error}>{errorMessage}</p>
          ) : loading ? (
            /* `aria-hidden`: the fact of loading is announced through the
               existing status region above, not through a second one here —
               this is decoration only. */
            <div aria-hidden="true" className={styles.loading} data-testid="search-panel-loading">
              <span className={styles.loadingBar} />
              <span className={styles.loadingBar} />
              <span className={styles.loadingBar} />
            </div>
          ) : (
            <>
              {hasRows && (
                <ul
                  aria-label={`${fieldLabel} suggestions`}
                  className={styles.results}
                  id={listboxId}
                  role="listbox"
                >
                  {/* An ungrouped section renders its rows bare. A grouped one
                      is the APG grouped listbox: a nested `ul[role=group]`
                      named by a presentational header `li`, so a `ul` only
                      ever holds `li`s and arrow keys never land on a header. */}
                  {toSections(rows).map((section, sectionIndex) => (
                    section.group ? (
                      <li key={`${section.group}-${sectionIndex}`} role="presentation">
                        <ul
                          aria-labelledby={`${id}-group-${sectionIndex}`}
                          className={styles.section}
                          role="group"
                        >
                          <li
                            className={`dds-eyebrow ${styles.sectionHeader}`}
                            id={`${id}-group-${sectionIndex}`}
                            role="presentation"
                          >
                            {section.group}
                          </li>
                          {section.rows.map(renderRow)}
                        </ul>
                      </li>
                    ) : (
                      section.rows.map(renderRow)
                    )
                  ))}
                </ul>
              )}

              {!hasRows && answered && emptyMessage && query.length > 0 && (
                <p className={styles.empty}>{emptyMessage}</p>
              )}
            </>
          )
        )}

        {/* ⚠ EVERY state goes down this one channel, including the empty one: a
            live region transitioning to "" announces nothing at all, which is
            WCAG 2.2 SC 4.1.3 Status Messages (AA) in the state a global-nav
            search hits constantly. Computed as one string, so there is one
            place to read to know what is announced. */}
        <span aria-live="polite" className="dds-sr-only" role="status">
          {status}
        </span>
      </div>
    </div>
  );
}
export const SearchPanel = withSafeRender(SearchPanelImpl, "SearchPanel");

export default SearchPanel;
